import cn from 'classnames'
import { format } from 'date-fns'
import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { NavLink, useNavigate } from 'react-router-dom'

import useComponentVisible from 'hooks/useComponentVisible'
import { useStores } from 'hooks/useStores'
import { useGetRolesQuery, useGetSystemsQuery } from 'redux/services/common'
import { AuthApi } from 'services/api/AuthApi'
import { AuthStorage } from 'services/storage/AuthStorage'

import { ROUTER_NAMES } from 'router/routerNames'

import { NAV_LIST } from 'constants/nav'
import { UserRole } from 'models/user'

import { ReactComponent as Logo } from 'assets/images/svg/logo.svg'

import styles from './AppHeader.module.scss'

export const AppHeader: React.FC = observer(() => {
  const { data: roles = [] } = useGetRolesQuery()
  const { data: systems = [] } = useGetSystemsQuery(undefined)
  const { userStore } = useStores()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)
  const {
    ref: logoMenuRef,
    isComponentVisible: isLogoMenuOpen,
    setIsComponentVisible: setIsLogoMenuOpen,
  } = useComponentVisible<HTMLDivElement>(false)

  const onLogout = async () => {
    try {
      await AuthApi.logout()
    } finally {
      userStore.setUser(null)
      AuthStorage.clearTokens()
      navigate(ROUTER_NAMES.AUTH)
    }
  }

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const closeMenu = () => {
    setIsMenuOpen(false)
  }

  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMenuOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <header className={styles.container}>
      <div className={styles.center}>
        <div className={styles.logo} ref={logoMenuRef}>
          <button
            className={styles.logoTrigger}
            onClick={() => setIsLogoMenuOpen(!isLogoMenuOpen)}
          >
            <Logo />
          </button>

          {isLogoMenuOpen && systems.length > 0 ? (
            <div className={styles.logoMenu} role="menu">
              {systems.map(system => (
                <a
                  className={styles.logoMenuItem}
                  href={system.url}
                  key={system.code}
                  onClick={() => setIsLogoMenuOpen(false)}
                  role="menuitem"
                >
                  {system.name}
                </a>
              ))}
            </div>
          ) : null}
        </div>

        {userStore.user ? (
          <div className={styles.user}>
            <div className={styles.userName}>
              {userStore.user?.first_name} {userStore.user?.last_name}
            </div>
            <div className={styles.userRole}>
              {roles.find(r => r.code === userStore.user?.role)?.name}
              {userStore.user?.region?.name &&
                `(${userStore.user?.region?.name})`}
            </div>
            {userStore.user.last_login && (
              <span>
                {format(
                  new Date(userStore.user.last_login),
                  'yyyy-MM-dd HH:mm',
                )}
              </span>
            )}
          </div>
        ) : null}
        <div className={cn(styles.nav, { [styles.active]: isMenuOpen })}>
          <ul>
            {[
              UserRole.Administrator,
              UserRole.ONLY_VIEW,
              UserRole.Controller,
              UserRole.SuperController,
              UserRole.AdministratorCIB,
              UserRole.ControllerUKC,
              UserRole.Executor,
              UserRole.Specialist,
            ].includes(userStore.user?.role as UserRole)
              ? NAV_LIST.filter(item =>
                  item.roles.includes(userStore.user?.role as UserRole),
                ).map(item => {
                  return (
                    <li key={item.path + item.title}>
                      <NavLink
                        reloadDocument={item.reloadDocument}
                        className={props =>
                          cn(
                            styles.link,
                            props.isActive ? styles.linkActive : null,
                          )
                        }
                        to={item.path}
                        onClick={closeMenu}
                      >
                        <div>{item.icon}</div> <span>{item.title}</span>
                      </NavLink>
                    </li>
                  )
                })
              : null}
            {userStore.user ? (
              <li className={styles.logout}>
                <button className={styles.link} onClick={onLogout}>
                  Выйти
                </button>
              </li>
            ) : null}
          </ul>
        </div>

        <div
          className={cn(styles.hamburger, { [styles.active]: isMenuOpen })}
          onClick={toggleMenu}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </header>
  )
})
